import ApplicationController from '../controllers/application_controller'
import Logger from '../lib/logger'
import {
  getTheme,
  updateThemeCSS,
  applyThemeToCalendar,
  ensureModalZIndexCSS,
  cleanupThemeCSS
} from '../utils/flatpickr_theme'

export default class extends ApplicationController {
  static values = {
    dateFormat: { type: String, default: "Y-m-d" },
    enableTime: { type: Boolean, default: false },
    time24hr: { type: Boolean, default: true },
    allowInput: { type: Boolean, default: true },
    clickOpens: { type: Boolean, default: true },
    locale: { type: String, default: "en" }
  }

  static targets = ["input"]

  connect() {
    super.connect()

    this.constructor.updateFlatpickrThemeCSS().then(() => {
      this.ensureZIndexForModal()

      const targetInput = this.getInputElement()
      if (targetInput) {
        this.createFlatpickrInstance(targetInput)
      }
    })

    this.setupThemeListener()
  }

  disconnect() {
    super.disconnect()

    if (this.flatpickrInstance) {
      try {
        this.flatpickrInstance.destroy()
      } catch (error) {
        Logger.error("Flatpickr cleanup failed:", error)
      }
    }

    this.removeModalScrollListeners()

    if (this.themeObserver) {
      this.themeObserver.disconnect()
    }

    if (this.calendarObserver) {
      this.calendarObserver.disconnect()
      this.calendarObserver = null
    }

    cleanupThemeCSS()
  }

  isInModal(element) {
    if (!element) return false
    return !!element.closest('.modal, [role="dialog"], [data-bs-toggle="modal"]')
  }

  isScrollable(element) {
    if (!element) return false
    const style = getComputedStyle(element)
    return element.scrollHeight > element.clientHeight ||
      style.overflowY === 'auto' ||
      style.overflowY === 'scroll'
  }

  getModalScrollContainer(element) {
    if (!element) return null
    const modal = element.closest('.modal, [role="dialog"], [data-bs-toggle="modal"]')
    if (!modal) return null

    const containers = [
      modal.querySelector('.modal-body'),
      modal.querySelector('.modal-content'),
      modal
    ]

    for (const container of containers) {
      if (container && this.isScrollable(container)) {
        return container
      }
    }

    return containers.find(c => c) || modal
  }

  setupThemeListener() {
    if (window.matchMedia) {
      this.themeListener = () => {
        if (this.flatpickrInstance) {
          this.updateFlatpickrTheme()
        }
      }

      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', this.themeListener)
    }

    this.themeObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'data-bs-theme') {
          this.updateFlatpickrTheme()
        }
      }
    })

    if (document.documentElement) {
      this.themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-bs-theme']
      })
    }
  }

  cleanupFlatpickrTheme() {
    if (this.flatpickrInstance) return false
    return true
  }

  async updateFlatpickrTheme() {
    if (!this.flatpickrInstance) return

    try {
      await this.constructor.updateFlatpickrThemeCSS()
      await new Promise(resolve => setTimeout(resolve, 100))

      this.applyThemeToCalendar()
      this.ensureZIndexForModal()

      if (!this.flatpickrInstance.isOpen) {
        this.flatpickrInstance.destroy()
        this.flatpickrInstance = null

        const targetInput = this.getInputElement()
        if (targetInput) {
          this.removeModalScrollListeners()
          this.createFlatpickrInstance(targetInput)
        }
      }
    } catch (error) {
      Logger.error("Error updating flatpickr theme:", error)
    }
  }

  getInputElement() {
    if (this.hasInputTarget && this.inputTarget) {
      return this.inputTarget
    }

    if (this.element.tagName === 'INPUT') {
      return this.element
    }

    const flatpickrInput = this.element.querySelector('input[data-flatpickr-date-format], input[data-flatpickr-enable-time]')
    if (flatpickrInput) return flatpickrInput

    const firstInput = this.element.querySelector('input')
    if (firstInput) return firstInput

    Logger.error("No suitable input element found for flatpickr")
    return null
  }

  createFlatpickrInstance(inputElement) {
    if (typeof window.flatpickr === 'undefined') {
      Logger.error("Flatpickr library not available globally")
      return null
    }

    const inModal = this.isInModal(inputElement)
    let position = 'fixed'
    let container = inputElement.parentElement

    if (inModal) {
      container = this.getModalScrollContainer(inputElement) || inputElement.parentElement
    }

    const options = {
      dateFormat: this.dateFormatValue,
      enableTime: this.enableTimeValue,
      time_24hr: this.time24hrValue,
      allowInput: this.allowInputValue,
      clickOpens: this.clickOpensValue,
      locale: this.localeValue,
      disableMobile: true,
      onChange: (selectedDates, dateStr) => {
        this.dispatch("change", { detail: { date: dateStr, dates: selectedDates } })
      },
      position,
      container,
      zIndex: 99999,
      ariaLabel: "Date picker"
    }

    try {
      this.flatpickrInstance = window.flatpickr(inputElement, options)
      inputElement._flatpickrInstance = this.flatpickrInstance

      if (this.flatpickrInstance.calendarContainer) {
        const observer = new MutationObserver(() => {
          if (this.flatpickrInstance?.isOpen) {
            this.applyThemeToCalendar()
          }
        })

        observer.observe(this.flatpickrInstance.calendarContainer, {
          attributes: true,
          attributeFilter: ['class', 'style']
        })

        this.calendarObserver = observer

        setTimeout(() => {
          this.applyThemeToCalendar()
        }, 100)
      }

      if (inModal) {
        this.setupModalScrollListener(inputElement)
      }
    } catch (error) {
      Logger.error("Error creating flatpickr instance:", error)
    }
  }

  _callFlatpickrMethod(methodName, ...args) {
    if (!this.flatpickrInstance || typeof this.flatpickrInstance[methodName] !== 'function') {
      return
    }
    try {
      this.flatpickrInstance[methodName](...args)
    } catch (error) {
      Logger.error(`Error calling flatpickr ${methodName}:`, error)
    }
  }

  close() {
    this._callFlatpickrMethod('close')
  }

  clear() {
    this._callFlatpickrMethod('clear')
  }

  setDate(date) {
    this._callFlatpickrMethod('setDate', date)
  }

  reinitialize() {
    if (this.flatpickrInstance) {
      try {
        this.flatpickrInstance.destroy()
      } catch (error) {
        Logger.error("Error during flatpickr cleanup:", error)
      }
      this.flatpickrInstance = null
    }

    this.removeModalScrollListeners()
    cleanupThemeCSS()

    setTimeout(() => {
      const targetInput = this.getInputElement()
      if (targetInput) {
        this.createFlatpickrInstance(targetInput)
      }
    }, 50)
  }

  setupModalScrollListener(inputElement) {
    this.removeModalScrollListeners()

    const scrollContainer = this.getModalScrollContainer(inputElement)
    if (!scrollContainer) return

    this.boundHandleModalScroll = this.handleModalScroll.bind(this)
    this.scrollContainer = scrollContainer

    const modal = inputElement.closest('.modal, [role="dialog"]')
    if (modal) {
      this.modalElement = modal
    }

    const events = [
      { element: scrollContainer, event: 'scroll' },
      { element: window, event: 'resize' }
    ]

    if (modal) {
      events.push({ element: modal, event: 'shown.bs.modal' })
      events.push({ element: modal, event: 'hidden.bs.modal' })
    }

    for (const { element, event } of events) {
      element.addEventListener(event, this.boundHandleModalScroll, { passive: true })
    }
  }

  handleModalScroll() {
    if (!this.flatpickrInstance?.isOpen) return

    this.applyThemeToCalendar()

    if (this.flatpickrInstance._positionCalendar) {
      try {
        this.flatpickrInstance._positionCalendar()
      } catch {
        // flatpickr handles positioning internally
      }
    }
  }

  applyThemeToCalendar() {
    if (!this.flatpickrInstance?.calendarContainer) return
    applyThemeToCalendar(this.flatpickrInstance.calendarContainer)
  }

  ensureZIndexForModal() {
    if (!this.flatpickrInstance?.calendarContainer) return

    const inputElement = this.getInputElement()
    if (inputElement && this.isInModal(inputElement)) {
      ensureModalZIndexCSS()
    }

    if (this.flatpickrInstance.calendarContainer) {
      this.flatpickrInstance.calendarContainer.style.zIndex = '99999'
    }
  }

  removeModalScrollListeners() {
    if (!this.boundHandleModalScroll) return

    const events = [
      { element: this.scrollContainer, event: 'scroll' },
      { element: window, event: 'resize' }
    ]

    if (this.modalElement) {
      events.push({ element: this.modalElement, event: 'shown.bs.modal' })
      events.push({ element: this.modalElement, event: 'hidden.bs.modal' })
    }

    for (const { element, event } of events) {
      if (element) {
        element.removeEventListener(event, this.boundHandleModalScroll)
      }
    }

    this.scrollContainer = null
    this.modalElement = null
    this.boundHandleModalScroll = null
  }

  /* -- Static theme management, delegated to utils/flatpickr_theme.js -- */

  static getTheme() {
    return getTheme()
  }

  static updateFlatpickrThemeCSS() {
    return updateThemeCSS()
  }
}
